import { Menu, Moon, Sun, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTheme } from "../../context/ThemeContext";
import { navLinks } from "../../data/navLinks";
import { useExternalRedirect } from "../../hooks/useExternalRedirect";
import { MobileMenu } from "./MobileMenu";
import { NavLink } from "./NavLink";

const Navbar = () => {
  const { theme, toggleTheme } = useTheme();
  const { handleExternalRedirect } = useExternalRedirect();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const navbarRef = useRef<HTMLDivElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const targetProgress = useRef(0);
  const currentProgress = useRef(0);
  const lastUpdateTime = useRef(performance.now());

  const closeMenu = () => setIsMenuOpen(false);

  const handleNavLinkClick = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    closeMenu();
  };

  const calculateScrollProgress = () => {
    const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
    targetProgress.current = totalHeight > 0 ? (window.scrollY / totalHeight) * 100 : 0;
  };

  const smoothScrollProgress = (timestamp: number) => {
    // Limit updates to ~60 FPS (16.67ms per frame)
    if (timestamp - lastUpdateTime.current < 16.67) {
      rafRef.current = requestAnimationFrame(smoothScrollProgress);
      return;
    }
    lastUpdateTime.current = timestamp;

    // Linear interpolation
    const lerpFactor = 0.15; // Balanced for smoothness and responsiveness
    currentProgress.current +=
      (targetProgress.current - currentProgress.current) * lerpFactor;

    // Update state only if change is significant
    if (Math.abs(currentProgress.current - scrollProgress) > 0.1) {
      setScrollProgress(Math.round(currentProgress.current * 10) / 10); // Round to 1 decimal
    }

    rafRef.current = requestAnimationFrame(smoothScrollProgress);
  };

  useEffect(() => {
    // Throttle scroll events
    let scrollTimeout: NodeJS.Timeout;
    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        calculateScrollProgress();
      }, 50); // Throttle to 50ms
    };

    const handleNavigation = () => {
      calculateScrollProgress();
    };

    const handleResize = () => {
      calculateScrollProgress();
    };

    // Initial calculation
    calculateScrollProgress();

    // Start animation loop
    rafRef.current = requestAnimationFrame(smoothScrollProgress);

    // Add event listeners
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("popstate", handleNavigation);
    window.addEventListener("resize", handleResize);

    // Cleanup
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("popstate", handleNavigation);
      window.removeEventListener("resize", handleResize);
      clearTimeout(scrollTimeout);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        navbarRef.current &&
        !navbarRef.current.contains(event.target as Node)
      ) {
        closeMenu();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <nav
      ref={navbarRef}
      className="fixed top-0 w-full backdrop-blur-sm bg-white/70 dark:bg-black/70 text-black dark:text-white shadow-md z-50"
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          <Link
            to="/"
            className="flex items-center space-x-2"
            onClick={handleNavLinkClick}
          >
            <img
              src="https://cdn.thrivebot.in/review-user-avatars/thrivebot.webp"
              alt="Thrivebot Logo"
              className="h-8 w-8 object-contain"
              width={32}
              height={32}
              rel="preconnect"
              loading="lazy"
            />
            <span className="font-bold text-xl">Thrivebot</span>
          </Link>

          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          <div className="hidden md:flex items-center space-x-6">
            {navLinks.map((link) => (
              <NavLink
                key={link.text}
                {...link}
                handleExternalRedirect={handleExternalRedirect}
                onClick={handleNavLinkClick}
              />
            ))}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-800"
              aria-label={`Switch to ${
                theme === "dark" ? "light" : "dark"
              } mode`}
            >
              {theme === "dark" ? <Sun size={20} /> : <Moon size={20} />}
            </button>
          </div>
        </div>
      </div>
      <div
        className="h-1 bg-black dark:bg-white transition-transform duration-100 ease-out will-change-transform origin-left"
        style={{ transform: `scaleX(${scrollProgress / 100})` }}
      />
      <MobileMenu
        isOpen={isMenuOpen}
        theme={theme}
        toggleTheme={toggleTheme}
        navLinks={navLinks.map((link) => ({
          ...link,
          onClick: handleNavLinkClick,
        }))}
        handleExternalRedirect={handleExternalRedirect}
      />
    </nav>
  );
};

export default Navbar;